import React, { useEffect, useState } from 'react';
import { Observer } from 'mobx-react';
import {
  Typography,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
  Tooltip,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { format } from 'date-fns';
import { Link, useHistory } from 'react-router-dom';
import { css } from '@emotion/core';
import { toJS } from 'mobx';
import DealTransactions from './DealTransactions';
import { WideBox, ButtonFlex } from '../../styles/styles';
import { DealMappedStatus, STATUS_COLOR } from '../dealUtil';
import CustomTreeView from './CustomTreeView';
import { CustomTable, FilterPlaceholder } from '../../common/CustomTable';
import { DealStatusEnums } from '../../common/Enums';
import { useRootStore } from '../../../stores/BridgeProvider';
import { safeEval, filterPreference } from '../../util/UtilFunctions';
import { dateSort } from '../../util/TableUtils';
import permissionCheck from '../../util/PermCheck';
import { NumberHandler } from '../../util/NumberHandler';
import DealAuditLog from './DealAuditLog';
import ColoredButton from '../../util/ColoredButton';
import InvoiceDeal from './InvoiceDeal';

const columns = [
  {
    Header: 'Voucher #',
    accessor: 'VoucherNumber',
    id: 1,
    Filter: FilterPlaceholder,
  },
  {
    Header: 'Contact',
    accessor: 'ContactName',
    id: 2,
  },
  {
    Header: 'Date',
    accessor: (d) => format(d.VoucherDate, 'MM/DD/YYYY'),
    id: 3,
    sortType: dateSort,
  },
  {
    Header: 'Due Date',
    accessor: (d) => (d.DueDate ? format(d.DueDate, 'MM/DD/YYYY') : 'N/A'),
    id: 4,
    sortType: dateSort,
  },
  {
    Header: 'Total',
    Cell: ({ row }) =>
      row.original.Amount ? (
        <NumberHandler
          num={row.original.Amount}
          float={2}
          justify="flex-end"
          currency="$"
        />
      ) : (
        'N/A'
      ),
    accessor: (d) => (d.Amount ? d.Amount : 'N/A'),
    id: 5,
  },
  {
    Header: 'Amount Mapped',
    Cell: ({ row }) =>
      row.original.AmountMapped ? (
        <NumberHandler
          num={row.original.AmountMapped}
          float={2}
          justify="flex-end"
          currency="$"
        />
      ) : (
        'N/A'
      ),
    accessor: (d) => (d.AmountMapped ? d.AmountMapped : 'N/A'),
    id: 8,
  },
];

const DealHeaderTop = css`
  display: flex;
  align-items: flex-start;
  text-align: left;
  justify-content: space-between;
  .center-header {
    max-width: 550px;
  }
`;

const DealHeaderContainer = css`
  display: flex;
  justify-content: space-between;
  .dhLeft,
  .dhRight {
    display: flex;
    flex-direction: column;
    justify-content: center;
    .dhDateRow {
      display: flex;
      justify-content: space-between;
      width: 230px;
      margin: 5px 0px;
    }
    .dhDetails {
      text-align: right;
      font-size: 0.8rem;
      display: block;
      margin: 6px 0 3px;
    }
  }
  .dhLeft {
    justify-content: flex-end;
  }
`;

const AccountingContainer = css`
  //display: flex;
  .arAPColumnWrapper {
    display: flex;
  }
  .acColumn {
    width: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    .acItemRow {
      display: flex;
      justify-content: space-between;
      width: 300px;
      margin: 5px 0px;
    }
  }
  .accBalances {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    margin-bottom: 2rem;
  }
`;

const BreadcrumbContainer = css`
  width: calc(80% + 60px);
  min-width: 360px;
  max-width: 960px;
  display: flex;
  a,
  a:visited,
  a:hover,
  a:active {
    color: inherit;
  }
`;

const StatusBlurb = (info) => {
  const statusName = DealMappedStatus(info);
  const styleObj = {
    background: STATUS_COLOR[statusName],
    color: 'white',
    opacity: '.9',
    borderRadius: '1rem',
    padding: '5px 10px',
  };
  return <span style={styleObj}>{statusName}</span>;
};

const netIncome = (accountArray) => {
  const total = accountArray.reduce(
    (sum, { Name, DebitAmount, CreditAmount }) => {
      if (Name === 'Income' || Name === 'Expense') {
        return sum + (parseFloat(CreditAmount) - parseFloat(DebitAmount));
      }
      return sum;
    },
    0,
  );

  return total;
};

const escrowBalance = (accountArray) => {
  const total = accountArray.reduce(
    (sum, { Name, DebitAmount, CreditAmount }) => {
      if (Name === 'Asset' || Name === 'Liability') {
        return sum + (parseFloat(CreditAmount) - parseFloat(DebitAmount));
      }
      return sum;
    },
    0,
  );

  return total;
};

const grossIncome = (accountArray) => {
  const total = accountArray.reduce(
    (sum, { Name, DebitAmount, CreditAmount }) => {
      if (Name === 'Income') {
        return sum + (parseFloat(CreditAmount) - parseFloat(DebitAmount));
      }
      return sum;
    },
    0,
  );

  return total;
};
// Net income is the sum of
// For each of (Income, Expense)
// Credit - Debit

const DealDashboard = ({ deal, mappedInvoices, refreshDeal, reinitialize }) => {
  const history = useHistory();
  const { authStore, dataStore, orgStore, dealMapperStore } = useRootStore();
  const [anchorEl, setAnchorEl] = useState(null);
  const [dealInfo, setDealInfo] = useState(deal);
  const [confirm, setConfirm] = useState(false);
  const [auditLog, setAuditLog] = useState(false);

  const { userContactId } = orgStore;
  const { accountInfo } = authStore;
  const { NotificationPref } = accountInfo;

  useEffect(() => {
    authStore.retrieveUser();
  }, []);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setConfirm(false);
    setAuditLog(false);
  };

  const handleOpen = () => {
    setConfirm(true);
  };

  const handleOpenAuditLog = () => {
    setAuditLog(true);
  };

  const getDeal = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/deal/${deal.DealId}`,
      );
      setDealInfo(data);
    } catch (e) {
      console.error(e);
    }
  };
  const deleteDeal = async () => {
    try {
      const nPref = filterPreference(NotificationPref, 'Deal Status');
      const {
        DealId,
        DealNumber,
        Title,
        Description,
        ContractDate,
        TransactionDate,
        BillableDate,
        PayableDate,
        AgentContactId,
        ApprovedBy,
      } = deal;
      await authStore.request.post(`${process.env.REACT_APP_API}/api/deal`, {
        DealId,
        DealNumber,
        Title,
        Description,
        ContractDate,
        TransactionDate,
        BillableDate,
        PayableDate,
        AgentContactId,
        ApprovedBy,
        StatusId: 5,
        NotificationPref: nPref,
        Email: {
          address: accountInfo.Email,
          name: accountInfo.Name.split(' ')[0],
          status: DealStatusEnums[5],
        },
      });
      await Promise.resolve(getDeal());
      handleClose();
    } catch (error) {
      console.error(error);
    }
  };
  const approveDeal = async () => {
    try {
      const nPref = filterPreference(NotificationPref, 'Deal Status');
      const {
        DealId,
        DealNumber,
        Title,
        Description,
        ContractDate,
        TransactionDate,
        BillableDate,
        PayableDate,
        AgentContactId,
        ApprovedBy,
      } = deal;
      await authStore.request.post(`${process.env.REACT_APP_API}/api/deal`, {
        DealId,
        DealNumber,
        Title,
        Description,
        ContractDate,
        TransactionDate,
        BillableDate,
        PayableDate,
        AgentContactId,
        ApprovedBy,
        StatusId: 2,
        NotificationPref: nPref,
        Email: {
          address: accountInfo.Email,
          name: accountInfo.Name.split(' ')[0],
          status: DealStatusEnums[2],
        },
      });
      await Promise.resolve(getDeal());
    } catch (error) {
      console.error(error);
    }
  };
  const {
    DealId,
    DealNumber,
    Title,
    Description,
    PayableDate,
    ModifiedTimestamp,
    ContractDate,
    BillableDate,
    APAmount,
    ARAmount,
    APAmountMapped,
    ARAmountMapped,
    APAmountPaid,
    ARAmountPaid,
    CreatedByName,
    ATArray,
    AgentContactName,
    AgentContactId,
    TransactionDate,
  } = deal;
  // const exists = val => val !== undefined && val !== null;
  const exists = (val) => val || val === 0;
  const vpermit = permissionCheck('VOUCHER_S');

  const mappedInvCheck = () => {
    if (permissionCheck('DEAL_S_AGENT')) {
      return (
        userContactId === AgentContactId || permissionCheck('VOUCHER_S_DEAL')
      );
    }
    return permissionCheck('VOUCHER_S_DEAL');
  };

  return (
    <Observer
      render={() => {
        return (
          <div
            style={{
              maxWidth: '1400px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div css={BreadcrumbContainer}>
              <Breadcrumbs
                aria-label="breadcrumb"
                style={{ marginTop: '1.5rem', alignSelf: 'flex-start' }}
              >
                <Link to="/dashboard">Dashboard</Link>
                <Link to="/deals">Deals</Link>
                <Link to={`/deal/${DealId}`} aria-current="page">
                  {Title && Title.length > 25
                    ? `${Title.slice(0, 25)}...`
                    : Title}
                </Link>
              </Breadcrumbs>
            </div>
            <WideBox
              style={{ height: 'auto', minHeight: 'auto', marginTop: '5px' }}
            >
              <div css={DealHeaderTop}>
                <div className="center-header">
                  <Typography variant="h2" gutterBottom>
                    <b>{Title}</b> - {DealNumber}
                  </Typography>
                  <Box>
                    <Typography variant="h5">
                      Status: {dealInfo.StatusName}
                    </Typography>
                  </Box>

                  <Box style={{ marginTop: '0.5rem' }}>
                    <Typography variant="body1">{Description}</Typography>
                  </Box>
                </div>
                <div>
                  {permissionCheck('DM_M') && (
                    <InvoiceDeal deal={deal} reinitialize={reinitialize} />
                  )}
                  {dealInfo.StatusId !== 2 && dealInfo.StatusId !== 5 && (
                    <ColoredButton
                      onClick={() => approveDeal()}
                      variant="outlined"
                      color="#1A936F"
                      style={{ marginRight: '10px' }}
                    >
                      Approve Deal
                    </ColoredButton>
                  )}
                  <IconButton
                    onClick={(e) => handleClick(e)}
                    style={{
                      padding: 0,
                      height: '24px',
                      alignContent: 'flex-start',
                      marginLeft: '10px',
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </div>
              </div>
              <div css={DealHeaderContainer}>
                <div className="dhLeft">
                  <div className="dhDateRow">
                    <span>
                      <b>Contract Date:</b>
                    </span>
                    <span>{format(ContractDate, 'MM/DD/YYYY')}</span>
                  </div>

                  <div className="dhDateRow" style={{ marginBottom: '0px' }}>
                    <span>
                      <b>Transaction Date:</b>
                    </span>
                    <span>{format(TransactionDate, 'MM/DD/YYYY')}</span>
                  </div>
                </div>
                <div className="dhRight">
                  <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem component={Link} to={`/deal/${DealId}/edit`}>
                      Edit Deal
                    </MenuItem>
                    {permissionCheck('DM_M') && (
                      <div>
                        <MenuItem
                          component={Link}
                          style={{ marginRight: '0px' }}
                          to={{
                            pathname: '/deal-mapper',
                            state: { dealIds: [DealId] },
                          }}
                        >
                          Open in Mapper
                        </MenuItem>
                      </div>
                    )}
                    {dealInfo.StatusId !== 5 && (
                      <MenuItem onClick={() => handleOpen()}>
                        Delete Deal
                      </MenuItem>
                    )}

                    <MenuItem onClick={() => handleOpenAuditLog()}>
                      Audit Log
                    </MenuItem>
                  </Menu>
                  {AgentContactName && (
                    <div className="dhDetails">Agent: {AgentContactName}</div>
                  )}
                  <div className="dhDetails">Created by: {CreatedByName}</div>
                  <div className="dhDetails">
                    Last modified: {format(ModifiedTimestamp, 'MM/DD/YYYY')}
                  </div>
                </div>
              </div>
              <Dialog open={confirm} onClose={handleClose}>
                <DialogTitle>Delete Deal?</DialogTitle>
                <DialogContent>
                  You are about to delete this deal. This deal will be archived
                  with a deleted status. Proceed?
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} color="primary">
                    Cancel
                  </Button>
                  <Button onClick={() => deleteDeal()} color="secondary">
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>

              <Dialog
                open={auditLog}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
              >
                <DialogContent>
                  <DealAuditLog />
                </DialogContent>
              </Dialog>
            </WideBox>
            <WideBox
              style={{
                height: 'auto',
                minHeight: 'auto',
                textAlign: 'inherit',
              }}
            >
              <div css={AccountingContainer}>
                {ATArray && (
                  <div className="accBalances">
                    <Box>
                      <Tooltip
                        title="The amount of all transactions with “Income” GL account types. Configure your GL Account defaults in Transaction Category setup."
                        placement="top"
                        arrow
                      >
                        <Typography variant="h5">
                          <NumberHandler
                            label="Gross Income: &nbsp;"
                            num={grossIncome(ATArray)}
                            currency="$"
                            justify="center"
                          />
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Box>
                      <Tooltip
                        title="Gross Income, less the amount of all transactions with “Expense” GL account types. Configure your GL Account defaults in Transaction Category setup."
                        placement="top"
                        arrow
                      >
                        <Typography variant="h5">
                          {ATArray.length === 0 ? (
                            'Invalid Transactions'
                          ) : (
                            <NumberHandler
                              label="Net Income: &nbsp;"
                              num={netIncome(ATArray)}
                              currency="$"
                              justify="center"
                            />
                          )}
                        </Typography>
                      </Tooltip>
                    </Box>
                    {escrowBalance(ATArray) > 0 && (
                      <Box>
                        <Tooltip
                          title="The amount of all transactions with “Asset” or “Liability” GL account types. This may indicates that you will have a surplus or deficit of cash at the end of this deal. This should always be $0, unless your company plans on retaining a loan, inventory, or some other asset or liability after the deal is through."
                          placement="top"
                          arrow
                        >
                          <Typography variant="h5">
                            <NumberHandler
                              label="Escrow Schedule: &nbsp;"
                              num={escrowBalance(ATArray)}
                              currency="$"
                              justify="center"
                            />
                          </Typography>
                        </Tooltip>
                      </Box>
                    )}
                  </div>
                )}
                <div className="arAPColumnWrapper">
                  <div className="acColumn">
                    <Typography
                      variant="h4"
                      gutterBottom
                      style={{ marginBottom: '1rem' }}
                    >
                      Accounts Receivable
                    </Typography>
                    {StatusBlurb({
                      amount: ARAmount,
                      mapped: ARAmountMapped,
                      paid: ARAmountPaid,
                    })}
                    <div className="acItemRow">
                      <span>
                        <b>AR Amount:</b>
                      </span>
                      <span>
                        {exists(ARAmount) ? (
                          <NumberHandler
                            num={ARAmount}
                            currency="$"
                            float={2}
                          />
                        ) : (
                          'N/A'
                        )}
                      </span>
                    </div>
                    <div className="acItemRow">
                      <span>
                        <b>AR Amount Mapped:</b>
                      </span>
                      <span>
                        {exists(ARAmountMapped) ? (
                          <NumberHandler
                            num={ARAmountMapped}
                            currency="$"
                            float={2}
                          />
                        ) : (
                          'N/A'
                        )}
                      </span>
                    </div>
                    <div className="acItemRow">
                      <span>
                        <b>AR Amount Paid:</b>
                      </span>
                      <span>
                        {exists(ARAmountPaid) ? (
                          <NumberHandler
                            num={ARAmountPaid}
                            currency="$"
                            float={2}
                          />
                        ) : (
                          'N/A'
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="acColumn">
                    <Typography
                      variant="h4"
                      gutterBottom
                      style={{ marginBottom: '1rem' }}
                    >
                      Accounts Payable
                    </Typography>
                    {StatusBlurb({
                      amount: APAmount,
                      mapped: APAmountMapped,
                      paid: APAmountPaid,
                    })}
                    <div className="acItemRow">
                      <span>
                        <b>AP Amount:</b>
                      </span>
                      <span>
                        {exists(APAmount) ? (
                          <NumberHandler
                            num={APAmount}
                            currency="$"
                            float={2}
                          />
                        ) : (
                          'N/A'
                        )}
                      </span>
                    </div>
                    <div className="acItemRow">
                      <span>
                        <b>AP Amount Mapped:</b>
                      </span>
                      <span>
                        {exists(APAmountMapped) ? (
                          <NumberHandler
                            num={APAmountMapped}
                            currency="$"
                            float={2}
                          />
                        ) : (
                          'N/A'
                        )}
                      </span>
                    </div>
                    <div className="acItemRow">
                      <span>
                        <b>AP Amount Paid:</b>
                      </span>
                      <span>
                        {exists(APAmountPaid) ? (
                          <NumberHandler
                            num={APAmountPaid}
                            currency="$"
                            float={2}
                          />
                        ) : (
                          'N/A'
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <CustomTreeView ATArray={ATArray} />
            </WideBox>

            <WideBox
              style={{ height: 'auto', minHeight: 'auto' }}
              showoverflow={1}
            >
              <DealTransactions DealId={DealId} refreshDeal={refreshDeal} />
            </WideBox>
            {mappedInvCheck() && (
              <WideBox style={{ height: 'auto', minHeight: 'auto' }}>
                <CustomTable
                  data={mappedInvoices || []}
                  columns={columns}
                  disableQueryParams
                  minRows={1}
                  trClassName="react-table-hover-row"
                  noDataText="No mapped invoices"
                  tableHeader={
                    <Typography variant="h4">Mapped Invoices</Typography>
                  }
                  getTrProps={({ row }) => ({
                    onClick: () =>
                      vpermit &&
                      history.push(`/vouchers/${row.original.VoucherId}`),
                  })}
                />
              </WideBox>
            )}
          </div>
        );
      }}
    />
  );
};

export default DealDashboard;

//  <div className="dhDateRow">
//    <span>
//      <b>Transaction Date:</b>
//    </span>
//    <span>{format(TransactionDate, 'MM/DD/YYYY')}</span>
//  </div>;
//                    <div className="dhDateRow">
//                      <span>
//                        <b>Payable Date:</b>
//                      </span>
//                      <span>{format(PayableDate, 'MM/DD/YYYY')}</span>
//                    </div>;

//  <ColoredButton
//    style={{ marginRight: '10px' }}
//    component={Link}
//    color="#01A1E8"
//    to={{
//      pathname: '/deal-mapper',
//      state: { dealIds: [DealId], invoiceDeal: true },
//    }}
//  >
//    Invoice Deal
//  </ColoredButton>;
